import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import client from "@/lib/api";
import { PageHeader } from "@/components/shared";
import { InflowProductPicker } from "@/components/InflowProductPicker";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from "@/components/ui/command";
import { Check, ChevronsUpDown, Loader2, UserSearch, Tag } from "lucide-react";
import { cn } from "@/lib/utils";

const aud = (n) => (n == null ? "—" : n.toLocaleString("en-AU", { style: "currency", currency: "AUD" }));
const fmtDate = (s) => { if (!s) return "—"; try { return new Date(s).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" }); } catch { return s; } };

function CustomerPicker({ customer, onSelect }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const search = useQuery({
    queryKey: ["inflow-customers", q],
    queryFn: async () => (await client.get("/inflow/customers", { params: { q } })).data,
    enabled: open && q.trim().length > 0,
    staleTime: 30000,
  });
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" data-testid="pc-customer-picker" className={cn("w-full justify-between font-normal", !customer && "border-warning/50")}>
          <span className="flex min-w-0 items-center gap-2">
            {customer ? <Check className="h-3.5 w-3.5 shrink-0 text-success" /> : <UserSearch className="h-3.5 w-3.5 shrink-0 text-warning" />}
            <span className="truncate">{customer?.name || "Select an inFlow client…"}</span>
          </span>
          <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search inFlow clients…" value={q} onValueChange={setQ} data-testid="pc-customer-search" />
          <CommandList>
            {search.isLoading ? (
              <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Searching…</div>
            ) : (
              <>
                <CommandEmpty>{q.trim() ? "No clients found." : "Type a client name…"}</CommandEmpty>
                {(search.data || []).map((c) => (
                  <CommandItem key={c.id} value={c.id} data-testid={`pc-customer-option-${c.id}`} onSelect={() => { onSelect(c); setOpen(false); }}>
                    <div className="flex w-full items-center justify-between gap-2">
                      <span className="truncate">{c.name}</span>
                      {c.contact && <span className="shrink-0 text-xs text-muted-foreground">{c.contact}</span>}
                    </div>
                  </CommandItem>
                ))}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function PriceChecker() {
  const [customer, setCustomer] = useState(null);
  const [product, setProduct] = useState({ id: "pc", name: "", inflow_product_id: null, inflow_product_name: null, sku: null });
  const ready = customer?.id && product.inflow_product_id;

  const { data, isFetching, isError, refetch } = useQuery({
    queryKey: ["price-history", customer?.id, product.inflow_product_id],
    queryFn: async () => (await client.get("/inflow/price-history", { params: { customer_id: customer.id, product_id: product.inflow_product_id } })).data,
    enabled: !!ready,
  });
  const rows = data?.rows || [];
  const s = data?.summary;

  return (
    <div className="animate-fade-up">
      <PageHeader title="Price Checker" subtitle="See what a builder was billed for a product across their past inFlow invoices" testid="price-checker-header" />

      <div className="grid gap-4 sm:grid-cols-2">
        <div><label className="mb-1.5 block text-xs font-semibold text-muted-foreground">Client</label><CustomerPicker customer={customer} onSelect={setCustomer} /></div>
        <div><label className="mb-1.5 block text-xs font-semibold text-muted-foreground">Product / SKU</label><InflowProductPicker item={product} onSelect={(patch) => setProduct((p) => ({ ...p, ...patch }))} /></div>
      </div>

      {!ready ? (
        <div className="mt-8 grid place-items-center rounded-2xl border border-dashed border-border bg-white/60 py-16 text-center" data-testid="pc-empty">
          <Tag className="mb-2 h-7 w-7 text-slate-300" />
          <p className="text-sm text-muted-foreground">Pick a client and a product to see the billed-price history.</p>
        </div>
      ) : isFetching ? (
        <div className="mt-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : isError ? (
        <div className="mt-8 rounded-2xl border border-error/30 bg-error/5 p-6 text-center text-sm text-error" data-testid="pc-error">Couldn't reach inFlow. <button onClick={() => refetch()} className="underline">Retry</button></div>
      ) : (
        <div className="mt-6" data-testid="pc-results">
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[["Last price", s?.last], ["Lowest", s?.min], ["Highest", s?.max], ["Average", s?.avg]].map(([k, v]) => (
              <div key={k} className="rounded-2xl border border-slate-100 bg-white p-4 shadow-[0_2px_12px_rgba(35,52,90,0.04)]">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{k}</div>
                <div className="mt-1 font-display text-xl font-extrabold text-ink">{aud(v)}</div>
              </div>
            ))}
          </div>

          {rows.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-border bg-white/60 py-12 text-center text-sm text-muted-foreground" data-testid="pc-no-history">No past orders found where <b>{customer.name}</b> was billed this product.</p>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white" data-testid="pc-history-table">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr><th className="p-3">Date</th><th className="p-3">Order #</th><th className="p-3 text-right">Unit price</th><th className="p-3 text-right">Qty</th><th className="p-3">Status</th></tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="border-t border-slate-100" data-testid={`pc-row-${i}`}>
                      <td className="p-3 text-ink">{fmtDate(r.order_date)}</td>
                      <td className="p-3 font-mono text-xs text-muted-foreground">{r.order_number || "—"}</td>
                      <td className="p-3 text-right font-bold text-ink">{aud(r.unit_price)}</td>
                      <td className="p-3 text-right text-muted-foreground">{r.quantity}</td>
                      <td className="p-3"><span className={cn("rounded-full px-2 py-0.5 text-[11px] font-semibold", r.invoiced || r.completed ? "bg-[#059669]/10 text-[#059669]" : "bg-slate-100 text-slate-500")}>{r.invoiced || r.completed ? "Invoiced" : "Quote"}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
